import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';

export const OtpScreen: React.FC = () => {
  const location = useLocation();
  const initialIdentifier = (location.state as any)?.identifier || (location.state as any)?.email || (location.state as any)?.phone || '';
  const initialIdentifierType = (location.state as any)?.identifier_type || ((location.state as any)?.email ? 'email' : 'phone');
  const initialSentTo = (location.state as any)?.sent_to;
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [identifierType, setIdentifierType] = useState<string | undefined>(initialIdentifierType);
  const [sentTo, setSentTo] = useState<string | undefined>(initialSentTo);
  const [otp, setOtp] = useState('');
  const { verifyOtp, requestOtp, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const [resendCooldown, setResendCooldown] = useState(0);
  const { isDark } = useTheme();

  useEffect(() => {
    clearError();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await (identifierType === 'email'
        ? verifyOtp({ email: identifier, otp })
        : verifyOtp({ phone: identifier, otp }));
      navigate('/login', { replace: true, state: { verified: true } });
    } catch (err: any) {
      if (String(err?.message || '').toLowerCase().includes('already verified')) {
        navigate('/login', { replace: true });
      }
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      const resp = await requestOtp(identifier || '').catch(() => null);
      if (resp) {
        if (resp.sent_to) setSentTo(resp.sent_to);
        if (resp.identifier) setIdentifier(resp.identifier);
        if (resp.identifier_type) setIdentifierType(resp.identifier_type);
      }
      setResendCooldown(60);
    } catch { }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card} className="sc-animate-scale-in">
        <div style={styles.iconContainer}>
          <AnimatedIcon name="shield" size={32} color="var(--sc-brand-primary)" />
        </div>

        <h2 style={styles.title}>Confirm Email</h2>
        <p style={styles.subtitle}>
          Enter the 6-digit confirmation code sent to <strong style={{ color: 'var(--sc-text-primary)' }}>{sentTo || identifier}</strong>
        </p>

        {error && (
          <div style={styles.errorBox} className="sc-animate-toast-in">
            <AnimatedIcon name="warning" size={16} color="var(--sc-error)" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirmation Code</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={styles.otpInput}
              maxLength={6}
              autoFocus
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || otp.length < 6}
            style={{
              ...styles.submitBtn,
              opacity: (isLoading || otp.length < 6) ? 0.6 : 1,
            }}
            className="sc-press"
          >
            {isLoading ? 'Confirming...' : 'Confirm'}
          </button>
        </form>

        <div style={styles.resendSection}>
          <p style={styles.resendText}>Didn't receive the code?</p>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0}
            style={{
              ...styles.resendBtn,
              color: resendCooldown > 0 ? 'var(--sc-text-tertiary)' : 'var(--sc-brand-primary)',
              cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </div>

        <div style={styles.backSection}>
          <Link to="/login" style={styles.backLink}>
            <AnimatedIcon name="bell" size={14} color="var(--sc-brand-primary)" />
            <span style={{ marginLeft: 6 }}>Back to Sign In</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--sc-bg-primary)',
    fontFamily: 'var(--sc-font-family)',
    padding: 16,
  },
  card: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-xl)',
    padding: '40px 32px',
    width: '100%',
    maxWidth: 420,
    boxShadow: 'var(--sc-shadow-xl)',
    border: '1px solid var(--sc-border-light)',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: 'var(--sc-brand-primary-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: 'var(--sc-text-primary)',
    margin: '0 0 6px',
    fontFamily: 'var(--sc-font-display)',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--sc-text-secondary)',
    lineHeight: 1.5,
    margin: '0 0 28px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--sc-error-bg)',
    border: '1px solid var(--sc-error)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '12px 16px',
    color: 'var(--sc-error-text)',
    marginBottom: 20,
    textAlign: 'left',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--sc-text-secondary)',
    textAlign: 'left',
  },
  otpInput: {
    width: '100%',
    height: 56,
    border: '1px solid var(--sc-border)',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 28,
    letterSpacing: 8,
    fontWeight: 700,
    textAlign: 'center',
    background: 'var(--sc-bg-input)',
    color: 'var(--sc-text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all var(--sc-transition-fast)',
  },
  submitBtn: {
    height: 'var(--sc-touch-btn)',
    background: 'var(--sc-brand-gradient)',
    color: 'var(--sc-text-on-brand)',
    border: 'none',
    borderRadius: 'var(--sc-radius-md)',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'var(--sc-shadow-brand)',
    transition: 'all var(--sc-transition-fast)',
    marginTop: 10,
  },
  resendSection: {
    marginTop: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  resendText: {
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
    margin: 0,
  },
  resendBtn: {
    background: 'none',
    border: 'none',
    fontWeight: 700,
    fontSize: 13,
    fontFamily: 'inherit',
    padding: '4px 8px',
  },
  backSection: {
    marginTop: 28,
    paddingTop: 16,
    borderTop: '1px solid var(--sc-border-light)',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--sc-brand-primary)',
    fontWeight: 600,
    fontSize: 13,
    textDecoration: 'none',
  },
};
